// Pulls announcement messages from the announcements edge function and cycles
// through them at the top of the UI. Skips rendering on admin routes to avoid
// cluttering the dashboard.
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, ChevronRight, Megaphone } from 'lucide-react';

interface Announcement {
  id: string;
  message: string;
  url?: string;
  is_active: boolean;
  created_at: string;
}

export default function AnnouncementBar() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [fadeIn, setFadeIn] = useState(true);
  const location = useLocation();

  if (location.pathname.startsWith('/admin')) return null;

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
      const res = await fetch(`${supabaseUrl}/functions/v1/announcements`, {
        headers: anonKey
          ? {
              apikey: anonKey,
              Authorization: `Bearer ${anonKey}`,
            }
          : undefined,
      })
        .catch(() => null);

      if (!res || !res.ok) {
        setAnnouncements([]);
        return;
      }

      const { data } = await res.json().catch(() => ({ data: [] }));
      setAnnouncements(data || []);
    } catch {
      setAnnouncements([]);
    }
  };

  useEffect(() => {
    if (announcements.length < 2) return;

    const interval = setInterval(() => {
      setFadeIn(false);

      setTimeout(() => {
        setIndex((prev) => (prev + 1) % announcements.length);
        setFadeIn(true);
      }, 300);
    }, 6000);

    return () => clearInterval(interval);
  }, [announcements.length]);

  if (!visible || announcements.length === 0) return null;

  const currentAnnouncement = announcements[index];

  return (
    <div className="w-full bg-amber-100 border-b border-amber-200 text-amber-900 py-3 px-4 relative overflow-hidden">
      <button
        className="absolute right-4 top-1/2 -translate-y-1/2 text-amber-700 hover:text-amber-900 transition-colors z-10"
        onClick={() => setVisible(false)}
        aria-label="Dismiss announcement"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="max-w-7xl mx-auto pr-12">
        <div
          className={`transition-opacity duration-300 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}
        >
          {currentAnnouncement.url ? (
            <a
              href={currentAnnouncement.url}
              className="flex items-center justify-center gap-2 text-sm font-medium text-amber-800 hover:text-amber-900 hover:underline group"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Megaphone className="w-4 h-4 text-amber-600" />
              <span>{currentAnnouncement.message}</span>
              <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
          ) : (
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-amber-800">
              <Megaphone className="w-4 h-4 text-amber-600" />
              <span>{currentAnnouncement.message}</span>
            </div>
          )}
        </div>
      </div>

      {announcements.length > 1 && (
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1.5">
          {announcements.map((_, i) => (
            <div
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-all ${
                i === index ? 'bg-amber-700' : 'bg-amber-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
